import React, { useState } from 'react';
import { Heart, MessageCircle, Share2, Radio, Users, BookOpen, CheckCircle2, Send } from 'lucide-react';
import { communityPosts, liveKajian } from '../data/mockData';

const AVATAR_COLORS = {
  SR: 'from-pink-500 to-rose-600',
  AF: 'from-blue-500 to-indigo-600',
  DA: 'from-purple-500 to-violet-600',
  MR: 'from-green-500 to-emerald-600',
  FZ: 'from-[#00C9B1] to-[#00A896]',
  BS: 'from-[#F5A623] to-[#D4881A]',
};

function PostCard({ post }) {
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(post.likes);

  const handleLike = () => {
    if (liked) {
      setLikeCount(prev => prev - 1);
    } else {
      setLikeCount(prev => prev + 1);
    }
    setLiked(!liked);
  };

  const colorClass = AVATAR_COLORS[post.user.avatar] || 'from-gray-500 to-gray-600';

  return (
    <div
      data-testid={`post-card-${post.id}`}
      className="rounded-2xl p-5 dark:bg-[#1A1A24] bg-white dark:border dark:border-[#2D3748] border border-[#E2E8F0] card-hover"
    >
      {/* User Info */}
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colorClass} flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}>
          {post.user.avatar}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold dark:text-white text-[#1A202C] text-sm">{post.user.name}</p>
            {post.user.verified && (
              <CheckCircle2 size={14} className="text-[#00C9B1] flex-shrink-0" />
            )}
            {post.package && (
              <span className="text-[10px] font-medium bg-[#F5A623]/10 text-[#F5A623] px-2 py-0.5 rounded-full border border-[#F5A623]/20">
                {post.package}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <p className="text-xs dark:text-gray-500 text-gray-400">{post.user.location}</p>
            <span className="text-xs dark:text-gray-600 text-gray-300">·</span>
            <p className="text-xs dark:text-gray-500 text-gray-400">{post.time}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <p className="text-sm dark:text-gray-300 text-[#2D3748] leading-relaxed mb-4">
        {post.content}
      </p>

      {/* Tags */}
      {post.tags && post.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {post.tags.map(tag => (
            <span key={tag} className="tag-pill">
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-1 pt-3 border-t dark:border-[#2D3748] border-[#E2E8F0]">
        <button
          data-testid={`like-btn-${post.id}`}
          onClick={handleLike}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl transition-all text-xs font-medium ${
            liked
              ? 'text-red-400 bg-red-400/10'
              : 'dark:text-gray-500 text-gray-400 dark:hover:text-red-400 hover:text-red-400 dark:hover:bg-red-400/10 hover:bg-red-50'
          }`}
        >
          <Heart size={14} className={liked ? 'fill-red-400' : ''} />
          <span>{likeCount}</span>
        </button>
        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium dark:text-gray-500 text-gray-400 dark:hover:text-[#00C9B1] hover:text-[#00C9B1] dark:hover:bg-[#00C9B1]/10 hover:bg-teal-50 transition-all">
          <MessageCircle size={14} />
          <span>{post.comments}</span>
        </button>
        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium dark:text-gray-500 text-gray-400 dark:hover:text-blue-400 hover:text-blue-400 dark:hover:bg-blue-400/10 hover:bg-blue-50 transition-all">
          <Share2 size={14} />
          <span>Bagikan</span>
        </button>
      </div>
    </div>
  );
}

export default function Komunitas() {
  const [postContent, setPostContent] = useState('');
  const [activeTab, setActiveTab] = useState('trending');

  const tabs = [
    { id: 'trending', label: 'Trending' },
    { id: 'terbaru', label: 'Terbaru' },
    { id: 'tips', label: 'Tips Umroh' },
  ];

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold dark:text-white text-[#1A202C] mb-1">Komunitas Jamaah</h2>
        <p className="text-sm dark:text-gray-400 text-gray-500">Berbagi pengalaman dan saling mendoakan bersama 50K+ jamaah</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Feed - 2 cols */}
        <div className="lg:col-span-2 space-y-4">
          {/* Post Composer */}
          <div data-testid="post-composer" className="rounded-2xl p-4 dark:bg-[#1A1A24] bg-white dark:border dark:border-[#2D3748] border border-[#E2E8F0]">
            <div className="flex gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F5A623] to-[#D4881A] flex items-center justify-center text-[#0E0E12] font-bold text-sm flex-shrink-0">
                BS
              </div>
              <div className="flex-1">
                <textarea
                  data-testid="post-textarea"
                  value={postContent}
                  onChange={(e) => setPostContent(e.target.value)}
                  placeholder="Bagikan pengalaman atau tips umroh Anda..."
                  rows={2}
                  className="w-full text-sm resize-none outline-none bg-transparent dark:text-gray-300 text-[#2D3748] dark:placeholder-gray-600 placeholder-gray-400"
                />
                <div className="flex items-center justify-between pt-3 border-t dark:border-[#2D3748] border-[#E2E8F0] mt-2">
                  <div className="flex items-center gap-1">
                    {['#TipsUmroh', '#Jamaah', '#Umroh'].map(t => (
                      <button key={t} onClick={() => setPostContent(prev => prev + ' ' + t)} className="text-[10px] text-[#00C9B1] hover:underline">{t}</button>
                    ))}
                  </div>
                  <button
                    data-testid="post-submit-btn"
                    disabled={!postContent.trim()}
                    className="flex items-center gap-2 px-4 py-2 bg-[#00C9B1] text-[#0E0E12] font-semibold text-xs rounded-xl hover:brightness-110 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Send size={12} />
                    Posting
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                data-testid={`community-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-[#00C9B1] text-[#0E0E12]'
                    : 'dark:bg-[#1A1A24] bg-white dark:border dark:border-[#2D3748] border border-[#E2E8F0] dark:text-gray-400 text-gray-500'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Posts */}
          <div data-testid="community-feed" className="space-y-4">
            {communityPosts.map(post => (
              <PostCard key={post.id} post={post} />
            ))}
          </div>
        </div>

        {/* Sidebar - 1 col */}
        <div className="space-y-4">
          {/* Live Kajian */}
          <div data-testid="live-kajian-widget" className="rounded-2xl overflow-hidden dark:bg-[#1A1A24] bg-white dark:border dark:border-[#2D3748] border border-[#E2E8F0]">
            {/* Thumbnail */}
            <div className="relative h-36 overflow-hidden dark:bg-[#242430] bg-gray-100">
              <img
                src={liveKajian.thumbnail}
                alt="Kajian"
                className="w-full h-full object-cover"
                onError={(e) => { e.target.style.display = 'none' }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0E0E12]/80 to-transparent" />

              {/* LIVE Badge */}
              <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-[#00C9B1] text-[#0E0E12] px-3 py-1 rounded-full">
                <Radio size={10} className="animate-live-pulse" />
                <span className="text-[10px] font-black tracking-wider">LIVE</span>
              </div>

              {/* Viewers */}
              <div className="absolute top-3 right-3 flex items-center gap-1 bg-[#0E0E12]/70 backdrop-blur-sm px-2 py-1 rounded-full">
                <Users size={10} className="text-white" />
                <span className="text-[10px] text-white font-medium">{liveKajian.viewers.toLocaleString()}</span>
              </div>

              {/* Play Button */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-12 h-12 rounded-full bg-[#00C9B1]/90 flex items-center justify-center cursor-pointer hover:scale-110 transition-transform">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#0E0E12">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="p-4">
              <p className="text-xs text-[#00C9B1] font-semibold uppercase tracking-wider mb-1">Kajian Live Sekarang</p>
              <h4 className="font-bold dark:text-white text-[#1A202C] text-sm leading-snug mb-1">{liveKajian.title}</h4>
              <p className="text-xs dark:text-gray-400 text-gray-500 mb-3">{liveKajian.speaker}</p>
              <button
                data-testid="join-kajian-btn"
                className="w-full py-2.5 bg-[#00C9B1] text-[#0E0E12] font-semibold text-xs rounded-xl hover:brightness-110 transition-all flex items-center justify-center gap-2"
              >
                <Radio size={12} className="animate-live-pulse" />
                Gabung Sekarang
              </button>
            </div>
          </div>

          {/* Upcoming Schedule */}
          <div data-testid="upcoming-schedule" className="rounded-2xl p-4 dark:bg-[#1A1A24] bg-white dark:border dark:border-[#2D3748] border border-[#E2E8F0]">
            <p className="text-xs font-semibold dark:text-gray-400 text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
              <BookOpen size={12} />
              Jadwal Kajian Berikutnya
            </p>
            <p className="text-sm font-semibold dark:text-white text-[#1A202C] mb-1">Fiqih Haji & Umroh</p>
            <p className="text-xs dark:text-gray-500 text-gray-400 mb-2">Ust. Dr. Khalid Al-Marhomy</p>
            <p className="text-xs text-[#F5A623] font-medium">{liveKajian.nextSchedule}</p>
          </div>

          {/* Community Stats */}
          <div className="rounded-2xl p-4 dark:bg-[#1A1A24] bg-white dark:border dark:border-[#2D3748] border border-[#E2E8F0]">
            <p className="text-xs font-semibold dark:text-gray-400 text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Users size={12} />
              Statistik Komunitas
            </p>
            <div className="space-y-3">
              {[
                { label: 'Total Anggota', value: '52.341', color: 'text-[#00C9B1]' },
                { label: 'Post Hari Ini', value: '1.247', color: 'text-[#F5A623]' },
                { label: 'Kajian Live', value: '12 Sesi', color: 'text-purple-400' },
              ].map(stat => (
                <div key={stat.label} className="flex items-center justify-between">
                  <span className="text-xs dark:text-gray-400 text-gray-500">{stat.label}</span>
                  <span className={`text-sm font-bold ${stat.color}`}>{stat.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
